<template>
    <form class="video-form" novalidate @submit.prevent="onSubmit">
        <p class="form-required-note"><span aria-hidden="true">*</span> Required fields</p>

        <section class="form-section mb-4" aria-labelledby="placement-heading">
            <div class="section-heading">
                <div>
                    <p class="section-eyebrow">EVENT</p>
                    <h2 id="placement-heading">Where these videos belong</h2>
                </div>
                <i class="pi pi-calendar" aria-hidden="true"></i>
            </div>

            <div class="form-row">
                <div class="form-group col-md-3">
                    <label for="eventYear">Year <span class="required-indicator" aria-hidden="true">*</span></label>
                    <select id="eventYear" v-model="model.eventYear" class="form-control" @change="onYearChange">
                        <option value="" disabled>Select year</option>
                        <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                    </select>
                </div>
                <div class="form-group col-md-9">
                    <label for="eventId">Event <span class="required-indicator" aria-hidden="true">*</span></label>
                    <select id="eventId" v-model="model.eventId" class="form-control"
                            :class="{ 'is-invalid': errors.eventId }" :disabled="!model.eventYear" @change="onEventChange">
                        <option value="" disabled>{{ eventPlaceholder }}</option>
                        <option v-for="item in events" :key="item.value" :value="item.value">{{ item.label }}</option>
                    </select>
                    <div v-if="errors.eventId" class="invalid-feedback">{{ errors.eventId }}</div>
                </div>
            </div>
            <div class="form-group mb-0">
                <label for="eventDetailId">Session</label>
                <select id="eventDetailId" v-model="model.eventDetailId" class="form-control"
                        aria-describedby="session-help" :disabled="!model.eventId">
                    <option value="">No session: highlights, aftermovie, or the whole event</option>
                    <!-- Grouped by day with the start time in front: names such as "Dinner" repeat across days. -->
                    <optgroup v-for="day in sessionDays" :key="day.key" :label="day.label">
                        <option v-for="item in day.sessions" :key="item.value" :value="item.value">{{ item.optionLabel }}</option>
                    </optgroup>
                </select>
                <small id="session-help" class="form-help">{{ sessionHelp }}</small>
            </div>
        </section>

        <section class="form-section" aria-labelledby="videos-heading">
            <div class="section-heading">
                <div>
                    <p class="section-eyebrow">YOUTUBE</p>
                    <h2 id="videos-heading">{{ isNew ? `Videos (${model.items.length})` : 'Video' }}</h2>
                </div>
                <i class="pi pi-video" aria-hidden="true"></i>
            </div>

            <p v-if="isNew" class="section-intro">Paste a YouTube link: the title and thumbnail fill in on their own.</p>

            <div v-for="(item, index) in model.items" :key="item.key" class="video-row"
                 :class="{ 'has-error': rowHasError(index) }" :data-row="index">
                <div v-if="isNew" class="video-row-header">
                    <h3 class="video-row-title">Video {{ index + 1 }}</h3>
                    <button v-if="model.items.length > 1" type="button" class="btn btn-link btn-sm remove-row"
                            :aria-label="`Remove video ${index + 1}`" @click="removeRow(index)">
                        <i class="pi pi-trash mr-1" aria-hidden="true"></i>Remove
                    </button>
                </div>

                <div class="video-row-body">
                    <div class="video-preview" :class="{ 'is-empty': !videoIdOf(item) }">
                        <img v-if="videoIdOf(item)" :src="thumbnailOf(item)" :alt="item.title || 'Video thumbnail'"/>
                        <p v-else class="preview-hint">
                            <i class="pi pi-video" aria-hidden="true"></i>
                            Preview appears after you paste a link
                        </p>
                    </div>

                    <div class="video-fields">
                        <div class="form-group">
                            <label :for="`url-${item.key}`">YouTube link <span class="required-indicator" aria-hidden="true">*</span></label>
                            <InputText :id="`url-${item.key}`" v-model="item.videoUrl" class="form-control"
                                       :class="{ 'is-invalid': rowError(index, 'videoUrl') }"
                                       placeholder="https://www.youtube.com/watch?v=..." @blur="onUrlBlur(item)"/>
                            <div v-if="rowError(index, 'videoUrl')" class="invalid-feedback d-block">{{ rowError(index, 'videoUrl') }}</div>
                        </div>
                        <div class="form-group">
                            <label :for="`title-${item.key}`">Title <span class="required-indicator" aria-hidden="true">*</span></label>
                            <InputText :id="`title-${item.key}`" v-model="item.title" class="form-control"
                                       :class="{ 'is-invalid': rowError(index, 'title') }" maxlength="255"
                                       :placeholder="item.fetchingTitle ? 'Fetching the title from YouTube...' : ''"/>
                            <div v-if="rowError(index, 'title')" class="invalid-feedback d-block">{{ rowError(index, 'title') }}</div>
                        </div>
                        <div class="detail-fields">
                            <div class="form-group mb-0">
                                <label :for="`desc-${item.key}`">Description</label>
                                <Textarea :id="`desc-${item.key}`" v-model="item.description" class="form-control" rows="2"
                                          :class="{ 'is-invalid': rowError(index, 'description') }" maxlength="500"/>
                                <div v-if="rowError(index, 'description')" class="invalid-feedback d-block">{{ rowError(index, 'description') }}</div>
                            </div>
                            <div class="form-group mb-0 date-field">
                                <label :for="`date-${item.key}`">Date</label>
                                <Calendar :inputId="`date-${item.key}`" v-model="item.videoDate" dateFormat="dd/mm/yy"
                                          :showIcon="true" :showButtonBar="true" class="w-100"
                                          :placeholder="fallbackDate ? fallbackDate.placeholder : ''"
                                          :aria-describedby="`date-help-${item.key}`"/>
                                <small :id="`date-help-${item.key}`" class="form-help">{{ dateHelp }}</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <button v-if="isNew" type="button" class="btn btn-outline-primary btn-sm add-row" @click="addRow">
                <i class="pi pi-plus mr-1" aria-hidden="true"></i>Add another video
            </button>
        </section>

        <div class="form-actions">
            <p v-if="formError" class="form-alert" role="alert">{{ formError }}</p>
            <div class="form-actions-buttons">
                <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="onCancel">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                    <i v-if="saving" class="pi pi-spin pi-spinner mr-1" aria-hidden="true"></i>
                    {{ submitLabel }}
                </button>
            </div>
        </div>

        <ConfirmDialog/>
    </form>
</template>

<script>
import {defineComponent} from 'vue'
import moment from 'moment'
import apiService from '@/services/ApiService'
import {eventYearOptions} from '@/utils/eventYears'
import {fetchYoutubeTitle, parseYoutubeId, youtubeThumbnailUrl} from '@/utils/youtube'

const INVALID_FIELDS_MESSAGE = 'Review the highlighted fields.';

let rowKey = 0;

function newItem(values = {}) {
    rowKey += 1;
    return {
        key: rowKey,
        videoUrl: '',
        title: '',
        description: '',
        videoDate: null,
        fetchingTitle: false,
        ...values
    };
}

export default defineComponent({
    name: "VideoGalleryForm",
    props: {
        id: {
            type: String,
            required: false
        },
        isNew: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            model: {
                eventYear: '',
                eventId: '',
                eventDetailId: '',
                items: [newItem()]
            },
            years: eventYearOptions(),
            events: [],
            loadingEvents: false,
            sessions: [],
            errors: {},
            formError: '',
            serverErrorRow: null,
            pendingTitles: [],
            saving: false,
            hasUnsavedChanges: false
        }
    },
    computed: {
        sessionDays() {
            const days = [];
            this.sessions.forEach(session => {
                const start = moment(session.startDate);
                const key = start.format('YYYY-MM-DD');
                let day = days.find(d => d.key === key);
                if (!day) {
                    day = {key, label: start.format('dddd, D MMMM YYYY'), sessions: []};
                    days.push(day);
                }
                // Non-breaking spaces: browsers collapse ordinary runs of spaces inside <option>.
                day.sessions.push({...session, optionLabel: `${start.format('HH:mm')}\u00a0\u00a0${session.label}`});
            });
            return days;
        },
        selectedSession() {
            return this.sessions.find(x => x.value === this.model.eventDetailId) || null;
        },
        selectedEvent() {
            return this.events.find(x => x.value === this.model.eventId) || null;
        },
        sessionHelp() {
            if (!this.model.eventId) {
                return 'Choose an event first to see its sessions.';
            }
            if (!this.selectedSession) {
                return 'Pick a session when the video records one part of the programme.';
            }
            const start = moment(this.selectedSession.startDate);
            const end = moment(this.selectedSession.endDate);
            const endFormat = end.isSame(start, 'day') ? 'HH:mm' : 'dddd, D MMMM YYYY, HH:mm';
            return `${start.format('dddd, D MMMM YYYY, HH:mm')} to ${end.format(endFormat)}`;
        },
        /** The date the API stores when a video's date is left empty, shown so the admin can see it. */
        fallbackDate() {
            if (this.selectedSession) {
                return {placeholder: moment(this.selectedSession.startDate).format('DD/MM/YYYY'), source: 'session'};
            }
            if (this.selectedEvent && this.selectedEvent.startDate) {
                return {placeholder: moment(this.selectedEvent.startDate, 'YYYY-MM-DD').format('DD/MM/YYYY'), source: 'event'};
            }
            return null;
        },
        dateHelp() {
            return this.fallbackDate
                ? `Leave empty to use the ${this.fallbackDate.source} date shown.`
                : 'Empty uses the session date, or the event date when there is no session.';
        },
        eventPlaceholder() {
            if (!this.model.eventYear) {
                return 'Select a year first';
            }
            return this.loadingEvents || this.events.length ? 'Select event' : `No events in ${this.model.eventYear}`;
        },
        submitLabel() {
            if (this.saving) {
                return 'Saving...';
            }
            if (!this.isNew) {
                return 'Save changes';
            }
            const count = this.model.items.length;
            return count === 1 ? 'Save video' : `Save ${count} videos`;
        }
    },
    watch: {
        model: {
            deep: true,
            handler() {
                this.hasUnsavedChanges = true;
                this.serverErrorRow = null;
                if (Object.keys(this.errors).length && this.validate() && this.formError === INVALID_FIELDS_MESSAGE) {
                    this.formError = '';
                }
            }
        }
    },
    methods: {
        videoIdOf(item) {
            return parseYoutubeId(item.videoUrl);
        },
        thumbnailOf(item) {
            return youtubeThumbnailUrl(this.videoIdOf(item));
        },
        rowError(index, field) {
            return this.errors[`items.${index}.${field}`];
        },
        rowHasError(index) {
            return this.serverErrorRow === index ||
                ['videoUrl', 'title', 'description'].some(field => this.rowError(index, field));
        },
        /** Brings the first problem into view, so an error in video 6 of 8 is not missed. */
        async revealFirstError() {
            await this.$nextTick();
            const target = this.$el.querySelector('.is-invalid') || this.$el.querySelector('.video-row.has-error');
            if (!target) {
                return;
            }
            const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            target.scrollIntoView?.({block: 'center', behavior: reduceMotion ? 'auto' : 'smooth'});
            (target.matches('input, select, textarea') ? target : target.querySelector('input'))?.focus({preventScroll: true});
        },
        onUrlBlur(item) {
            const videoId = this.videoIdOf(item);
            if (!videoId || item.title.trim()) {
                return;
            }

            item.fetchingTitle = true;
            const request = fetchYoutubeTitle(videoId).then(title => {
                item.fetchingTitle = false;
                // The admin may have typed a title while the request was in flight; keep theirs.
                if (title && !item.title.trim() && this.videoIdOf(item) === videoId) {
                    item.title = title.slice(0, 255);
                }
            });
            this.pendingTitles.push(request);
            request.finally(() => {
                this.pendingTitles = this.pendingTitles.filter(x => x !== request);
            });
        },
        addRow() {
            this.model.items.push(newItem());
        },
        removeRow(index) {
            this.model.items.splice(index, 1);
        },
        onYearChange() {
            this.model.eventId = '';
            this.model.eventDetailId = '';
            this.sessions = [];
            return this.loadEvents(this.model.eventYear);
        },
        onEventChange() {
            this.model.eventDetailId = '';
            return this.loadSessions(this.model.eventId);
        },
        loadEvents(year) {
            this.events = [];
            if (!year) {
                return Promise.resolve();
            }
            this.loadingEvents = true;
            return apiService.get(`/VideoGallery/events/${year}`)
                .then(response => {
                    this.events = response.data;
                })
                .catch(error => this.toastError('Failed to load events', error))
                .finally(() => {
                    this.loadingEvents = false;
                });
        },
        loadSessions(eventId) {
            this.sessions = [];
            if (!eventId) {
                return Promise.resolve();
            }
            return apiService.get(`/VideoGallery/sessions/${eventId}`)
                .then(response => {
                    this.sessions = response.data;
                })
                .catch(error => this.toastError('Failed to load sessions', error));
        },
        validate() {
            const errors = {};
            if (!this.model.eventId) {
                errors.eventId = 'Select the event these videos belong to.';
            }

            const seen = {};
            this.model.items.forEach((item, index) => {
                const videoId = this.videoIdOf(item);
                if (!videoId) {
                    errors[`items.${index}.videoUrl`] = item.videoUrl.trim()
                        ? 'This is not a YouTube video link.'
                        : 'YouTube URL is required.';
                } else if (seen[videoId] !== undefined) {
                    errors[`items.${index}.videoUrl`] = `Same video as Video ${seen[videoId] + 1}.`;
                } else {
                    seen[videoId] = index;
                }

                if (!item.title.trim()) {
                    errors[`items.${index}.title`] = 'Title is required.';
                }
                if ((item.description || '').trim().length > 500) {
                    errors[`items.${index}.description`] = 'Description can be at most 500 characters.';
                }
            });

            this.errors = errors;
            return Object.keys(errors).length === 0;
        },
        toPayloadItem(item) {
            return {
                videoUrl: item.videoUrl.trim(),
                title: item.title.trim(),
                description: (item.description || '').trim() || null,
                videoDate: item.videoDate ? moment(item.videoDate).format('YYYY-MM-DD') : null
            };
        },
        async onSubmit() {
            this.formError = '';
            this.serverErrorRow = null;
            this.saving = true;
            // Clicking Save blurs the URL field and may start a title lookup; wait for it rather
            // than failing "Title is required" on a title that is about to arrive.
            await Promise.all(this.pendingTitles);
            if (!this.validate()) {
                this.saving = false;
                this.formError = INVALID_FIELDS_MESSAGE;
                this.revealFirstError();
                return;
            }

            const eventDetailId = this.model.eventDetailId || null;
            const request = this.isNew
                ? apiService.post('/VideoGallery/add', {
                    eventId: this.model.eventId,
                    eventDetailId,
                    items: this.model.items.map(this.toPayloadItem)
                })
                : apiService.post('/VideoGallery/edit', {
                    id: this.id,
                    eventId: this.model.eventId,
                    eventDetailId,
                    ...this.toPayloadItem(this.model.items[0])
                });

            request
                .then(response => {
                    this.hasUnsavedChanges = false;
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Saved',
                        detail: this.isNew
                            ? `${response.data === 1 ? '1 video' : `${response.data} videos`} added to the gallery`
                            : 'Video has been updated',
                        life: 5000
                    });
                    this.$router.push('/video-gallery');
                })
                .catch(error => {
                    // Row-level messages from the API ("Baris 2: ...") name the row: show them as-is and
                    // mark that row, since it may be far from the save button.
                    this.formError = error.response?.data?.Message || 'The videos could not be saved. Try again.';
                    const row = /^Baris (\d+):/.exec(this.formError);
                    if (row && this.isNew) {
                        this.serverErrorRow = Number(row[1]) - 1;
                        this.revealFirstError();
                    }
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        onCancel() {
            if (!this.hasUnsavedChanges) {
                this.$router.push('/video-gallery');
                return;
            }

            this.$confirm.require({
                message: 'Discard the videos you have entered?',
                header: 'Discard changes',
                icon: 'pi pi-exclamation-triangle',
                acceptClass: 'p-button-danger',
                accept: () => this.$router.push('/video-gallery')
            });
        },
        toastError(summary, error) {
            this.$toast.add({
                severity: 'error',
                summary,
                detail: error.response?.data?.Message || 'Please try again.',
                life: 5000
            });
        },
        async loadVideo(id) {
            try {
                const {data} = await apiService.get(`/VideoGallery/${id}`);
                await Promise.all([this.loadEvents(data.eventYear), this.loadSessions(data.eventId)]);
                this.model = {
                    eventYear: data.eventYear,
                    eventId: data.eventId,
                    eventDetailId: data.eventDetailId || '',
                    items: [newItem({
                        videoUrl: data.videoUrl,
                        title: data.title,
                        description: data.description || '',
                        videoDate: data.videoDate ? moment(data.videoDate, 'YYYY-MM-DD').toDate() : null
                    })]
                };
                await this.$nextTick();
                this.hasUnsavedChanges = false;
            } catch (error) {
                this.toastError('Failed to load video', error);
            }
        }
    },
    async mounted() {
        if (!this.isNew) {
            await this.loadVideo(this.id);
            return;
        }

        this.model.eventYear = new Date().getFullYear();
        await this.loadEvents(this.model.eventYear);
        if (this.events.length === 1) {
            this.model.eventId = this.events[0].value;
            await this.loadSessions(this.model.eventId);
        }
        await this.$nextTick();
        this.hasUnsavedChanges = false;
    }
})
</script>

<style scoped>
/* Tokens follow EventForm.vue so the two forms read as one product. */
.video-form {
    max-width: 1280px;
}

.form-required-note {
    color: #5f6570;
    font-size: 0.875rem;
    margin: 0 0 1rem;
}

.form-required-note span,
.required-indicator {
    color: #c62828;
    font-weight: 700;
}

.form-section {
    background: #ffffff;
    border: 1px solid #e2e6ec;
    border-radius: 0.65rem;
    box-shadow: 0 0.15rem 0.65rem rgba(58, 59, 69, 0.05);
    padding: 1.5rem;
}

.section-heading {
    align-items: flex-start;
    border-bottom: 1px solid #edf0f4;
    display: flex;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
}

.section-heading h2 {
    color: #2b2f36;
    font-size: 1.15rem;
    font-weight: 600;
    margin: 0;
}

.section-eyebrow {
    color: #426cc6;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    margin: 0 0 0.25rem;
}

.section-heading > .pi {
    color: #426cc6;
    font-size: 1.2rem;
    padding-top: 0.25rem;
}

.section-intro {
    color: #5f6570;
    font-size: 0.9rem;
    margin: -0.5rem 0 0.5rem;
}

.form-group label {
    color: #343a40;
    font-size: 0.9rem;
    font-weight: 600;
}

.form-help {
    color: #6c757d;
    display: block;
    font-size: 0.78rem;
    line-height: 1.35;
    margin-top: 0.35rem;
}

/* Rows are divided by a rule, not boxed: the section is already the container. */
.video-row {
    padding: 1rem 0 1.25rem;
}

.video-row + .video-row {
    border-top: 1px solid #edf0f4;
    padding-top: 1.25rem;
}

.video-row-header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    min-height: 2rem;
}

.video-row-title {
    color: #2b2f36;
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0;
}

.video-row.has-error .video-row-title {
    color: #c62828;
}

.remove-row {
    color: #b02a37;
    padding: 0.25rem 0.5rem;
}

.remove-row:hover,
.remove-row:focus-visible {
    color: #842029;
}

.video-row-body {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: 240px minmax(0, 1fr);
}

/* The thumbnail is the one thing on this page that confirms the admin pasted the right video. */
.video-preview {
    align-self: start;
    aspect-ratio: 16 / 9;
    background: #1f2229;
    border-radius: 0.5rem;
    overflow: hidden;
}

.video-preview img {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
}

.video-preview.is-empty {
    background: #f6f8fb;
    border: 1px dashed #c3cad6;
}

.preview-hint {
    align-items: center;
    color: #6c757d;
    display: flex;
    flex-direction: column;
    font-size: 0.8rem;
    gap: 0.4rem;
    height: 100%;
    justify-content: center;
    line-height: 1.3;
    margin: 0;
    padding: 0 1rem;
    text-align: center;
}

.preview-hint .pi {
    color: #9aa3b2;
    font-size: 1.4rem;
}

.video-fields {
    max-width: 720px;
}

/* Description and date share a line: optional details, so the row stays short in a long batch. */
.detail-fields {
    display: grid;
    gap: 1rem;
    grid-template-columns: minmax(0, 1fr) 240px;
}

.add-row {
    margin-top: 0.25rem;
}

.form-actions {
    align-items: center;
    border-top: 1px solid #e2e6ec;
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding-top: 1.25rem;
}

.form-alert {
    background: #fff1f1;
    border: 1px solid #f5c2c7;
    border-radius: 0.5rem;
    color: #9d2525;
    flex: 1;
    font-size: 0.9rem;
    margin: 0;
    padding: 0.6rem 1rem;
}

.form-actions-buttons {
    display: flex;
    flex-shrink: 0;
    gap: 0.5rem;
}

@media (max-width: 768px) {
    .form-section {
        padding: 1rem;
    }

    .video-row-body {
        gap: 1rem;
        grid-template-columns: 1fr;
    }

    .video-preview {
        max-width: 320px;
    }

    .detail-fields {
        grid-template-columns: 1fr;
    }

    .form-actions {
        align-items: stretch;
        flex-direction: column;
    }

    .form-actions-buttons .btn {
        flex: 1;
    }
}
</style>
